import { Link } from "react-router-dom";
import { Compass, Scale, HeartHandshake } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import CTABlock from "@/components/CTABlock";
import { DuckBadge } from "@/components/DuckMascot";

const CONFUSIONS = [
  "what they actually need",
  "whether DIY will work",
  "what professional treatment should cost",
  "what treatment should include",
  "what questions to ask",
];

const VALUES = [
  { icon: Compass, title: "Independent", body: "We're a homeowner resource first. If DIY solves it, we'll tell you — even if that means you don't hire anyone." },
  { icon: Scale, title: "Transparent", body: "No scare tactics, no mystery pricing, no fake urgency. Just the questions and context that help you decide." },
  { icon: HeartHandshake, title: "Family-aware", body: "We assume you're thinking about kids and pets, because most homeowners are. We build that into everything." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About"
        description="DuckTheBugs exists because pest control is unnecessarily confusing. We help homeowners understand the problem, decide DIY vs pro, and know what they're paying for."
        path="/about"
      />
      <section className="bg-forest-950 text-cream grain pt-[72px]">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "About" }]} />
          <div className="mt-6 flex items-center gap-4">
            <DuckBadge size={72} />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-duckgreen">Our story</span>
          </div>
          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] max-w-3xl">
            Pest control is unnecessarily confusing.
          </h1>
          <p className="mt-5 text-lg text-cream/80 max-w-2xl leading-relaxed">
            So we built the resource we wished existed — one that treats homeowners like adults and answers the questions everyone actually has.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
        <Reveal>
          <p className="text-xl leading-relaxed text-forest-950/85">
            When a pest problem shows up, most homeowners don't actually know:
          </p>
          <ul className="mt-6 space-y-3">
            {CONFUSIONS.map((c) => (
              <li key={c} className="flex items-start gap-3 text-lg text-forest-950/80">
                <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                {c}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xl leading-relaxed text-forest-950/85">
            DuckTheBugs exists to make those decisions easier. We help you understand what's going on, figure out whether you can handle it yourself, and — when it's genuinely time for a pro — know what good service looks like and connect with an appropriate local provider.
          </p>
          <div className="my-10 rounded-3xl bg-forest-950 text-cream p-8">
            <p className="font-serif text-2xl leading-snug italic text-cream/95">
              "Sometimes you need a pro. Sometimes you just need to dump the water out of a flowerpot. We'll help you figure out which."
            </p>
          </div>
          <p className="text-lg leading-relaxed text-forest-950/80">
            We're deliberately independent. DuckTheBugs doesn't operate treatment trucks and isn't the company applying pesticides. That independence is the point — it means our advice can put your interests first, whether that leads to a product recommendation, a DIY afternoon, or a call to a local pro.
          </p>
        </Reveal>
      </section>

      <section className="bg-sand grain">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-forest-950 mb-10">What we stand for</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full rounded-3xl bg-card border border-forest/10 p-7">
                  <div className="w-12 h-12 rounded-2xl bg-duckgreen/12 flex items-center justify-center mb-4">
                    <v.icon className="w-6 h-6 text-forest" />
                  </div>
                  <h3 className="font-serif text-xl font-bold text-forest-950">{v.title}</h3>
                  <p className="mt-2 text-forest-950/70 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 md:py-24">
        <CTABlock
          variant="amber"
          eyebrow="Start anywhere"
          title="What's bugging you?"
          body="Browse the pest library, dig into the DIY hub, or get matched with a local pro. However you start, we're on your side."
          primary={{ to: "/pests", label: "Explore pests" }}
          secondary={{ to: "/hire-a-pro", label: "Find a Pro" }}
        />
      </div>
    </div>
  );
}
